import _ from "lodash";
import {
  Chord,
  Tone
} from "./model/core";
import { assert } from "./utils/error";
import { ChordAliasPartition } from "./utils/ChordAliasPartition";
import { RydUserAccount } from "./accounts/model";
import {
  IntervalPattern,
  ScalePattern
} from "./model/scales";
import { RydFeatures } from "./utils/FeatureToggle";


type CacheType = { [key: string]: any };

// REMINDER: value for this env variable is set in the .env.{environement name} file
const CORE_API_SERVER = process.env.REACT_APP_CORE_API_SERVER || 'http://localhost:5100';
const CUSTOMER_SERVER = RydFeatures.Toggle.isOn("USE_FAKE_LOGIN")
  ? CORE_API_SERVER
  : (process.env.REACT_APP_CUSTOMER_SERVER || 'http://localhost:5300');

class ApiFactory {
  static create() {
    return new ApiClient({
      coreServer: CORE_API_SERVER,
      customerServer: CUSTOMER_SERVER
    });
  }
}
function convertAPIChordToModel(jsonOut: any): Chord {
  const family = jsonOut.name;
  const c = new Chord(Tone.fromIndex(jsonOut.root), family);
  c.tones = jsonOut.tones.map(Tone.fromIndex);
  return c;
}

class ApiClient {
  coreServer: string;
  customerServer: string;

  constructor({
    coreServer,
    customerServer
  }: {
    coreServer: string,
    customerServer: string
  }) {
    this.coreServer = coreServer;
    this.customerServer = customerServer;
  }

  /* Cache */
  private CHORDS_CACHE: CacheType = {};
  private CHORDS_BY_TONES_CACHE: CacheType = {};

  async __cached(cache: CacheType, f, k) {
    const cacheKey = JSON.stringify(k);

    if (cache[cacheKey]) return cache[cacheKey];

    const result = await f(k);
    cache[cacheKey] = result;
    return new Promise((resolve) => resolve(result));
  }

  async getChord(chord: Chord) {
    return this.__cached(this.CHORDS_CACHE, (c) => {
      const v = this._getChord(c).then(r => convertAPIChordToModel(r));
      return v;
    }, chord);
  }

  async _getChord(chord: Chord) {
    assert(chord, 'Chord is required');
    assert(chord.root, 'Chord root is required');
    assert(chord.family, `Chord family is required: was ${JSON.stringify(chord)}`);

    return this._post(this.coreServer, '/core-api/chord', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        root: chord.root.getName(),
        family: chord.family,
      })
    });
  }

  // async getNoteDurationsAsCountedIn({ duration, countedIn }) {
  //   return this._post(this.coreServer, '/core-api/static/note-durations/as-counted-in', {
  //     method: 'POST',
  //     headers: {
  //       'Accept': 'application/json',
  //       'Content-Type': 'application/json',
  //     },
  //     body: JSON.stringify({
  //       duration: duration,
  //       countedIn: countedIn
  //     })
  //   });
  // }

  loadChord(jsonOut: any): Chord | undefined {
    if (!jsonOut) return;
    if (jsonOut.length === 0) return;
    // FIX: we may have gotten multiple chords. user should know and be able to select.
    // FIXME: we are leaking the server model by calling family "name"
    if (!jsonOut[0]) {
      console.error("Invalid chord data received from server: ", jsonOut);
      return;
    }
    return convertAPIChordToModel(jsonOut[0]);
  }

  /**
   * FIXME: clients need to pass the output of the method to loadChord on the then() side of the promise.
   * @param notes
   * @returns
  */
  async getChordIdByNotes(notes: number[]): Promise<Chord> {

    const c = this.__cached(this.CHORDS_BY_TONES_CACHE, (n) => {
      const cs: Promise<Chord[]> = this._getChordIdByNotes(n);
      cs.then((partitionChords) => ChordAliasPartition.register(partitionChords.map((c) => convertAPIChordToModel(c))));
      return new Promise((resolve) => resolve(cs));
    }, notes);

    return c;
  }

  async _getChordIdByNotes(notes: number[]): Promise<Chord[]> {

    return this._post(this.coreServer, '/core-api/chord-id', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'notes',
        notes: notes
      })
    });
  }

  async getUserAccount(bearer: string): Promise<RydUserAccount> {
    // assert(customerNumber, 'Customer Number is required to get user account');

    return this._get(this.customerServer, `/current_user`, {

      'Authorization': `Bearer ${bearer}`,

    }).then((res) => {
      if (!res.ok) {
        console.error('Failed to fetch user account:', res.status, res.statusText);
        throw new Error(`Failed to fetch user account: ${res.status} ${res.statusText}`);
      }
      return res;
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data) {
          console.error('Failed to fetch user account:', data);
          throw new Error(`No user account found`);
        }
        return data as RydUserAccount;
      });
  }

  async authenticateUser(email: string, password: string): Promise<RydUserAccount> {
    assert(email, 'email is required for authentication');
    assert(password, 'Password is required for authentication');

    return this._post(this.customerServer, '/authenticate', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        password: password
      })
    })
      .then((data) => {

        if (!data) {
          throw new Error('Authentication failed');
        }
        return data as RydUserAccount;
      });
  }

  async logoutUser(token: string): Promise<void> {
    return this._post(this.customerServer, '/logout', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    }).then(() => {
      console.log('User logged out successfully');
    }).catch((error) => {
      console.error('Logout failed:', error);
    });
  }

  private async _get(server, path, headers = {}) {

    return fetch(`${server}${path}`, {
      method: 'GET',
      headers: headers
    });
  }

  private async _post(server, path, body): Promise<any> {

    return fetch(`${server}${path}`, body).then((res) => {
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}, message: ${res.statusText}`);
      }
      return res.json();
    });
  }
}

// const convertHNCToComponentModel = (hnc) => {
//   let notes = _.range(hnc.length).map(() => []);
//   _.each(hnc.notes, (noteGroups, at) => {
//     _.each(noteGroups, (noteGroup) => {
//       notes[at] = {
//         notes: noteGroup.note_group,
//         duration: NoteDuration.fromBackendValue(noteGroup.duration)
//       };
//     })
//   });
//   return notes;
// }

// function readTones(tones) {
//   return _.map(tones, (tone) => {
//     return tone;
//   });
// }

function readScales(scales): ScalePattern[] {
  return _.map(scales, (scale) => {
    return new ScalePattern(new IntervalPattern(scale.name, scale.intervals), scale.name);
  }
  );
}

export async function fetchStaticData(): Promise<{
  intervals: any[],
  noteDurations: any[],
  chordFamilies: any[],
  // scalePatterns: ScalePattern[]
}> {
  const intervals = await fetch(`${CORE_API_SERVER}/core-api/static/intervals`)
    .then((res) => res.json());
  const noteDurations = await fetch(`${CORE_API_SERVER}/core-api/static/note-durations`)
    .then((res) => res.json());
  const chordFamilies = await fetch(`${CORE_API_SERVER}/core-api/static/chord-families`)
    .then((res) => res.json());
  // const scalePatterns = await fetch(`${CORE_API_SERVER}/core-api/static/scales`)
  //   .then((res) => res.json())
  //   .then(readScales);

  return {
    intervals: intervals,
    noteDurations: noteDurations,
    chordFamilies: chordFamilies,
    // scalePatterns: scalePatterns
  };
}

export const API = ApiFactory.create();

export {
  // convertHNCToComponentModel,
  ApiClient,
  ApiFactory
};